<div class="p-4 sm:p-6 space-y-4 sm:space-y-5">
  <div class="flex items-center justify-between gap-3">
    <div class="min-w-0">
      <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
        {{ 'ADMIN.STAFF.TITLE' | translate }}
      </h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {{ 'ADMIN.STAFF.SUBTITLE' | translate }}
      </p>
    </div>
    <button
      (click)="openCreate()"
      class="shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
    >
      <lucide-icon [img]="UserPlus" class="w-4 h-4"></lucide-icon>
      <span class="hidden sm:inline">{{ 'ADMIN.STAFF.ADD' | translate }}</span>
    </button>
  </div>

  @if (loading()) {
    <div
      class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center"
    >
      <p class="text-gray-500">{{ 'ADMIN.STAFF.LOADING' | translate }}</p>
    </div>
  } @else {
    <!-- Mobile card view (hidden on sm+) -->
    <div class="sm:hidden space-y-3">
      @for (s of staff(); track s.id) {
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex items-center gap-3 min-w-0">
              <div
                class="w-8 h-8 shrink-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
              >
                {{ s.fullName.charAt(0).toUpperCase() }}
              </div>
              <div class="min-w-0">
                <p class="font-semibold text-gray-900 dark:text-white text-sm truncate">
                  {{ s.fullName }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ s.phoneNumber }}</p>
              </div>
            </div>
            <button
              (click)="openEdit(s)"
              class="shrink-0 inline-flex items-center justify-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Tahrirlash"
            >
              <lucide-icon [img]="Pencil" class="w-3.5 h-3.5"></lucide-icon>
            </button>
          </div>
          <div class="flex items-center justify-between text-xs">
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              [class]="roleClass(s.role)"
            >
              <lucide-icon [img]="Shield" class="w-3 h-3"></lucide-icon>
              {{ roleLabel(s.role) }}
            </span>
            <span class="text-gray-500 dark:text-gray-400">{{ formatDate(s.createdAt) }}</span>
          </div>
        </div>
      } @empty {
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center text-gray-500 text-sm"
        >
          {{ 'ADMIN.STAFF.EMPTY' | translate }}
        </div>
      }
    </div>

    <!-- Desktop table view (hidden on mobile) -->
    <div
      class="hidden sm:block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.STAFF.COLS.EMPLOYEE' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.STAFF.COLS.PHONE' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.STAFF.COLS.ROLE' | translate }}
              </th>
              <th
                class="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap"
              >
                {{ 'ADMIN.STAFF.COLS.JOINED' | translate }}
              </th>
              <th class="px-4 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            @for (s of staff(); track s.id) {
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <td class="px-4 py-3">
                  <div class="flex items-center gap-3">
                    <div
                      class="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    >
                      {{ s.fullName.charAt(0).toUpperCase() }}
                    </div>
                    <span class="font-medium text-gray-900 dark:text-white whitespace-nowrap">{{
                      s.fullName
                    }}</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {{ s.phoneNumber }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    [class]="roleClass(s.role)"
                  >
                    <lucide-icon [img]="Shield" class="w-3 h-3"></lucide-icon>
                    {{ roleLabel(s.role) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {{ formatDate(s.createdAt) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <button
                    (click)="openEdit(s)"
                    class="inline-flex items-center justify-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Tahrirlash"
                  >
                    <lucide-icon [img]="Pencil" class="w-3.5 h-3.5"></lucide-icon>
                  </button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="5" class="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                  {{ 'ADMIN.STAFF.EMPTY' | translate }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- Create Staff Dialog -->
@if (showCreateDialog()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ 'ADMIN.STAFF.FORM.TITLE' | translate }}
        </h2>
        <button
          (click)="showCreateDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form [formGroup]="createForm" (ngSubmit)="submitCreate()" class="p-5 space-y-4">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div>
          <label
            for=""
            for=""
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >{{ 'ADMIN.STAFF.FORM.FULL_NAME' | translate }}</label
          >
          <input
            formControlName="fullName"
            type="text"
            [placeholder]="'EMPLOYEES.FORM.NAME_HINT' | translate"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label
            for=""
            for=""
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >{{ 'ADMIN.STAFF.FORM.PHONE' | translate }}</label
          >
          <input
            formControlName="phoneNumber"
            type="text"
            placeholder="+998901234567"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label
            for=""
            for=""
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >{{ 'ADMIN.STAFF.FORM.PASSWORD' | translate }}</label
          >
          <input
            formControlName="password"
            type="password"
            [placeholder]="'ADMIN.STAFF.FORM.PASSWORD_MIN' | translate"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label
            for=""
            for=""
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >{{ 'ADMIN.STAFF.FORM.GENDER' | translate }}</label
          >
          <select
            formControlName="gender"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option [value]="0">{{ 'ADMIN.STAFF.FORM.MALE' | translate }}</option>
            <option [value]="1">{{ 'ADMIN.STAFF.FORM.FEMALE' | translate }}</option>
          </select>
        </div>
        <div>
          <label
            for=""
            for=""
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >{{ 'ADMIN.STAFF.FORM.ROLE' | translate }}</label
          >
          <select
            formControlName="role"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option [value]="2">{{ 'ADMIN.STAFF.FORM.ROLE_OPERATOR' | translate }}</option>
            <option [value]="5">{{ 'ADMIN.STAFF.FORM.ROLE_ADMIN' | translate }}</option>
          </select>
        </div>
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            (click)="showCreateDialog.set(false)"
            class="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {{ 'ADMIN.STAFF.FORM.CANCEL' | translate }}
          </button>
          <button
            type="submit"
            [disabled]="createForm.invalid || submitting()"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{
              submitting()
                ? ('ADMIN.STAFF.FORM.SAVING' | translate)
                : ('ADMIN.STAFF.FORM.ADD' | translate)
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Edit Staff Dialog -->
@if (showEditDialog()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
      <div
        class="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700"
      >
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Xodimni tahrirlash</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ selectedStaff()?.fullName }}
          </p>
        </div>
        <button
          (click)="showEditDialog.set(false)"
          class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <lucide-icon [img]="X" class="w-5 h-5 text-gray-500"></lucide-icon>
        </button>
      </div>
      <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="p-5 space-y-4">
        @if (errorMsg()) {
          <div
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400"
          >
            {{ errorMsg() }}
          </div>
        }
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.STAFF.FORM.FULL_NAME' | translate }}
          </label>
          <input
            formControlName="fullName"
            type="text"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.STAFF.FORM.PHONE' | translate }}
          </label>
          <input
            formControlName="phoneNumber"
            type="text"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.STAFF.FORM.PASSWORD' | translate }}
          </label>
          <input
            formControlName="password"
            type="password"
            placeholder="Bo'sh qoldirilsa o'zgarmaydi"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.STAFF.FORM.GENDER' | translate }}
          </label>
          <select
            formControlName="gender"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option [value]="0">{{ 'ADMIN.STAFF.FORM.MALE' | translate }}</option>
            <option [value]="1">{{ 'ADMIN.STAFF.FORM.FEMALE' | translate }}</option>
          </select>
        </div>
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ 'ADMIN.STAFF.FORM.ROLE' | translate }}
          </label>
          <select
            formControlName="role"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option [value]="2">{{ 'ADMIN.STAFF.FORM.ROLE_OPERATOR' | translate }}</option>
            <option [value]="5">{{ 'ADMIN.STAFF.FORM.ROLE_ADMIN' | translate }}</option>
          </select>
        </div>
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            (click)="showEditDialog.set(false)"
            class="px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Bekor qilish
          </button>
          <button
            type="submit"
            [disabled]="editForm.invalid || submitting()"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
          >
            {{ submitting() ? 'Saqlanmoqda...' : 'Saqlash' }}
          </button>
        </div>
      </form>
    </div>
  </div>
}
